import {
  buildContext,
  buildSearchEngine,
  ContextPayload,
  loadAdvancedSearchQueryActions,
  SearchEngine,
} from '@coveo/headless';
import { GetCoveoConfig, GetCoveoToken, GetFreshCoveoToken } from 'Feature/Coveo/Helpers/CoveoApi';
import { useUserData } from 'Foundation/User/UserContext';
import { IUserData } from 'Foundation/User/UserContextModels';
import { useState, useEffect, createContext, useContext, useRef } from 'react';
import {
  getUserQueryFilters,
  getDefaultUserStateFilter,
  getArchivedFilter,
  getPublishDateFilter,
} from './Helpers/UserHelper';
import { getCoveoFieldNameByString } from './Mappers/CoveoFieldMapper';
import { getCoveoSiteConfigByName } from './Mappers/CoveoSiteConfig';
import { ICoveoSearchConfig, PersonalizedFieldNames, ICoveoSiteConfig } from './Models/CoveoModels';

type CoveoSearchEngineContextType = {
  headlessEngine: SearchEngine<{}> | null | undefined;
};
const CoveoSearchEngineContext = createContext<any | undefined>(undefined);
CoveoSearchEngineContext.displayName = 'CoveoSearchEngineContext';
interface ICoveoSearchEngineProps {
  analyticsEnabled: boolean;
  children: any;
}
export function CoveoSearchEngineProvider(props: ICoveoSearchEngineProps) {
  const { userData } = useUserData();
  const headlessEngine = useRef<CoveoSearchEngineContextType | null>(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const getEngine = async () => {
      const engine = await CreateSearchEngine(userData, props.analyticsEnabled);
      if (engine) {
        headlessEngine.current = {
          headlessEngine: engine,
        };
        setShow(true);
        buildContext(engine!)?.set(GetUserContext(userData));
      } else {
        console.error("Can't load coveo engine so all coveo components won't load");
      }
    };
    if (userData && !headlessEngine.current) {
      getEngine();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!show) return null;
  return (
    <>
      <CoveoSearchEngineContext.Provider value={headlessEngine.current}>
        {props.children}
      </CoveoSearchEngineContext.Provider>
    </>
  );
}

export const GetUserContext = (userData: IUserData | undefined): ContextPayload => {
  let contextPayload = {
    state: userData?.state ?? '',
    role: userData?.selectedStatCodeData?.Role ?? '',
    tier: userData?.selectedStatCodeData?.Tier ?? '',
    gold: userData?.selectedStatCodeData?.AgencyServicingTypeCode ?? '',
    userType: userData?.userType ?? '',
  };
  return (contextPayload as ContextPayload) ?? {};
};

export function useCoveoSearchEngine(): CoveoSearchEngineContextType {
  const context = useContext(CoveoSearchEngineContext);
  if (context === undefined) {
    throw new Error(`useCoveoSearchEngine must be used within a CoveoSearchEngineProvider`);
  }
  return context;
}

async function CreateSearchEngine(userData: IUserData | undefined, analyticsEnabled: boolean) {
  const searchConfig: ICoveoSearchConfig | null = await GetCoveoConfig();
  if (!searchConfig || !searchConfig.organizationId || searchConfig.organizationId === '') {
    console.error('Coveo config not found');
    return null;
  }
  try {
    let token = await GetCoveoToken();
    if (token === '') {
      console.error('Coveo token not found');
      return null;
    }
    var headlessEngine = buildSearchEngine({
      configuration: {
        organizationId: searchConfig.organizationId,
        accessToken: token,
        renewAccessToken: GetFreshCoveoToken,
        search: {
          searchHub: 'iaportals',
        },
        analytics: {
          enabled: analyticsEnabled,
        },
      },
    });
    const aq = getAQForSite(searchConfig, userData);
    const advQuery = loadAdvancedSearchQueryActions(headlessEngine);
    const action = advQuery.registerAdvancedSearchQueries({ aq: aq });
    headlessEngine.dispatch(action);
    return headlessEngine;
  } catch {
    return null;
  }
}

function getAQForSite(searchConfig: ICoveoSearchConfig, userData: IUserData | undefined): string {
  let aq =
    '((@' +
    getCoveoFieldNameByString(PersonalizedFieldNames.source)?.coveoFieldName +
    '=="' +
    searchConfig.searchSource +
    '"' +
    ' AND (@' +
    getCoveoFieldNameByString(PersonalizedFieldNames.site)?.coveoFieldName +
    '=="' +
    searchConfig.siteName +
    '"';
  let coveoSiteConfig: ICoveoSiteConfig | undefined = getCoveoSiteConfigByName(searchConfig.siteName);
  // Additional sites that may return results for a given site. For example showing news content in safeco now
  if (coveoSiteConfig && coveoSiteConfig.search.additionalSites) {
    for (let site of coveoSiteConfig.search.additionalSites) {
      aq += ' OR @' + getCoveoFieldNameByString(PersonalizedFieldNames.site)?.coveoFieldName + '=="' + site + '"';
    }
  }
  aq += '))';
  // Any additional sources that may be returned for a given site. For example product guides returning for safeco now
  //TODO: remove check for source in query string post ia docs release
  if (coveoSiteConfig && coveoSiteConfig.search.additionalSources) {
    for (let source of coveoSiteConfig.search.additionalSources) {
      aq += ' OR (@' + getCoveoFieldNameByString(PersonalizedFieldNames.source)?.coveoFieldName + '=="' + source + '"';
      if (source == 'IADocs')
        aq += userData?.channel == 'BL' ? ' AND @lmcchannel == "BL"' : ' AND NOT @lmcchannel == "BL"'; //enforce channel for ia docs
    }
  }
  aq += '))';
  if (userData) {
    aq += getUserQueryFilters(userData);
    aq += getDefaultUserStateFilter(userData);
    let showArchived: string | null = new URLSearchParams(window.location.search)?.get('archived');
    aq += getArchivedFilter(showArchived === 'true');
    aq += getPublishDateFilter();
    if (showArchived) aq = "$sort(criteria: 'fielddescending', field: '@lmceffectivedate') " + aq;
  }
  return aq;
}
